import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Loader2, Save } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import RepProfileFields, { profileFrom, getMissingProfileFields } from '@/components/RepProfileFields';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import PageContainer from '@/components/PageContainer';

/**
 * Editing a rep's profile, its own page (task: profile edit as its own
 * page, 2026-09-08). Used to render inline inside the Admin list row,
 * which meant the row's own action buttons (promote, suspend, new
 * password...) and a full profile form were competing for the same small
 * card. Those actions stay in the list, one tap each, exactly as they
 * are; only the profile form (email plus everything RepProfileFields
 * owns) moves here.
 *
 * Same treatment as EditContact.jsx: back arrow, Cancel and Save together
 * at the top, the unsaved changes guard wired into the same global
 * NavigationGuardProvider so the bottom nav's Home/+/More catch an
 * in-progress edit here too, not just this page's own back arrow and
 * Cancel.
 */
function formFrom(u) {
  return {
    email: u?.email || '',
    // Task: split user Name into First/Last, 2026-09-20. New capability:
    // this page previously had no way to rename an existing rep at all,
    // only to set a name once at creation (Admin.jsx). Merged into the
    // same flat form object as email, not into RepProfileFields' own
    // value: like email, this is an identity field the admin edits here,
    // not a "rep profile" concern RepProfileFields owns, and Admin.jsx's
    // own create dialog already has its own separate First/Last inputs
    // rather than sharing this component for them.
    first_name: u?.first_name || '',
    last_name: u?.last_name || '',
    ...profileFrom(u),
  };
}

export default function EditRep() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const { data: rep, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => base44.entities.User.get(id),
    enabled: isAdmin,
  });

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => formFrom(rep));
  const [initialForm, setInitialForm] = useState(() => formFrom(rep));
  const [missingFields, setMissingFields] = useState([]);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);

  useUnsavedChangesGuard(isDirty);
  const { setGuard, guardedAction } = useNavigationGuard();

  useEffect(() => {
    if (rep) {
      const loaded = formFrom(rep);
      setForm(loaded);
      setInitialForm(loaded);
    }
  }, [rep]);

  const goBack = () => guardedAction(() => navigate(-1));

  const doSave = async () => {
    const missing = [
      ...(form.first_name.trim() ? [] : [{ key: 'first_name', label: 'First name' }]),
      ...(form.email.trim() ? [] : [{ key: 'email', label: 'Email' }]),
      // None of RepProfileFields' own required fields (cell_phone) apply to
      // a shared device account (task: simplify the device account
      // experience, 2026-09-16), the same reasoning Profile.jsx's own
      // device-account branch and the server (worker/lib/userProfile.ts)
      // already apply. Without this, an admin opening this page for the
      // shared booth login could never save even an unrelated edit, since
      // cell_phone here is always blank for that account.
      ...(rep?.is_device_account ? [] : getMissingProfileFields(form)),
    ];
    if (missing.length > 0) {
      setMissingFields(missing);
      return false;
    }

    setSaving(true);
    try {
      const { email, ...profile } = form;
      // Blanks are sent as empty strings, which the API turns into NULL, so
      // a field can be cleared as well as set.
      await base44.entities.User.update(id, { ...profile, email: email.trim().toLowerCase() });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      toast.success('Profile saved');
      // Resets the dirty check against what was just saved, not the values
      // this page loaded with (task: stay on the page after saving,
      // 2026-09-15). Without this, staying here after a successful save
      // would leave isDirty (and the unsaved changes guard) still reporting
      // the just-saved edit as unsaved.
      setInitialForm(form);
      return true;
    } catch (err) {
      toast.error(err.message || 'Could not save that profile');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // No longer navigates away on success (task: stay on the profile page
  // after saving, 2026-09-15): doSave's own toast is the confirmation, and
  // an admin editing a rep's numbers or calendar links one at a time no
  // longer gets bounced back to the Admin list after every single save.
  const handleSave = () => doSave();

  // Registers with the global guard (App.jsx's AppShellLayout) the same way
  // EditContact does, so the bottom nav intercepts navigation away from here
  // too, not just this page's own back arrow and Cancel.
  useEffect(() => {
    setGuard(isDirty, doSave);
    return () => setGuard(false, null);
  });

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <h1 className="text-xl font-bold text-foreground mb-1">Admins only</h1>
          <p className="text-sm text-muted-foreground mb-4">You don't have access to this page.</p>
          <Link to="/"><Button variant="outline">Back to app</Button></Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!rep) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-foreground font-medium">No such rep.</p>
        <Link to="/admin"><Button variant="outline">Back to Admin</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer className="px-4 py-6">
        {/* Same placement as the contact edit page: Back and Save together
            at the top, above the name, not in the bottom nav. Back goes
            through the same guardedAction as the nav's own Home/+/More, so
            an unsaved change is caught no matter which the rep taps.
            Global "Back" button (task: 2026-09-25): replaces the old round
            arrow icon plus a separate "Cancel" button, both of which called
            this identical goBack handler. Cancel is gone now that Back
            itself, in the same visual style Cancel used to have, already
            does that job. */}
        <div className="flex items-center gap-2 mb-4">
          <Button variant="outline" onClick={goBack} className="min-h-9 px-4 text-sm">Back</Button>
          <div className="flex-1" />
          <Button onClick={handleSave} disabled={saving || !isDirty} className="min-h-9 px-5 text-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" />Save</>}
          </Button>
        </div>

        <div className="mb-6">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Edit Rep Profile</p>
          <h1 className="text-2xl font-bold text-foreground truncate">{rep.full_name}</h1>
        </div>

        <div className="space-y-3">
          {/* Task: split user Name into First/Last, 2026-09-20. New
              capability: this page previously had no way to rename an
              existing rep at all, only to set a name once at creation
              (Admin.jsx). The server derives full_name from these two and
              keeps it in sync (worker/lib/userProfile.ts), so every
              existing display location that reads full_name (this page's
              own heading above included) needed no change. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">
                First name<span className="text-destructive"> *</span>
              </Label>
              <Input
                value={form.first_name}
                onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))}
                disabled={saving}
                className="mt-1 h-9 md:text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Last name</Label>
              <Input
                value={form.last_name}
                onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))}
                disabled={saving}
                className="mt-1 h-9 md:text-sm"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">
              Email<span className="text-destructive"> *</span>
            </Label>
            <Input
              type="email"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              disabled={saving}
              className="mt-1 h-9 md:text-sm"
            />
            {rep.status === 'invited' && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Not activated yet. Changing this retires their current invite link;
                use Resend invite on the Admin list to mail a fresh one to the corrected address.
              </p>
            )}
          </div>

          {/* A shared device account has no personal profile at all (task:
              simplify the device account experience, 2026-09-16): none of
              RepProfileFields' fields (title, phone, vCard, headshot,
              signature) will ever be used by a shared login, only email
              is real here. Same treatment as Profile.jsx's own branch for
              this. The "no calendar links" warning otherwise lives inside
              RepProfileFields' own Calendar Links section, right next to
              the actual list, for every other rep. */}
          {rep.is_device_account ? (
            <p className="text-xs text-muted-foreground">
              This is a shared device account, not a person. It has no personal profile: contacts
              captured under it are credited to whichever rep is chosen on the capture screen, not to
              this login.
            </p>
          ) : (
            <RepProfileFields
              value={form}
              onChange={setForm}
              disabled={saving}
            />
          )}

          {/* Same Save as the one at the top (task: a Save button at the
              bottom too, 2026-09-15), so a long form never needs a scroll
              back up just to save. */}
          <Button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="w-full min-h-10 bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save</>}
          </Button>
        </div>
      </PageContainer>

      {/* Named by label, not by field key, same pattern as Admin.jsx and
          Shows.jsx: a Save button that silently does nothing is worse than
          one that says exactly what it needs. */}
      <Dialog open={missingFields.length > 0} onOpenChange={(open) => { if (!open) setMissingFields([]); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Missing required fields
            </DialogTitle>
            <DialogDescription>Fill these in before saving:</DialogDescription>
          </DialogHeader>
          <ul className="space-y-1.5">
            {missingFields.map((field) => (
              <li key={field.key} className="flex items-center gap-2 text-sm text-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                {field.label}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button onClick={() => setMissingFields([])} className="w-full">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
